import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  Chip,
  Fab,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  PrimaryButton,
  CardContainer,
  FormInput,
  PageHeroBand,
  LoadingSkeleton,
  AppIconButton,
} from '../../components/ui';
import {
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { cashApi } from '../../api/cashApi';
import { financeApi } from '../../api/financeApi';
import { formatCurrency } from '../../config/currency';
import { CURRENCY } from '../../config/currency';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import CashMovementList from '../../components/finance/CashMovementList';
import type { CashMovement } from '../../components/finance/CashMovementList';
import PettyCashModal from '../../components/finance/PettyCashModal';
import type { PettyCashFormData } from '../../components/finance/PettyCashModal';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import RevenueBreakdownForm from '../../components/finance/RevenueBreakdownForm';
import { todayLocalIso } from '../../utils/dateLocal';

// --- Types ---

interface CashSession {
  id: number;
  status: 'OPEN' | 'CLOSED';
  openingCash: number;
  cashIn: number;
  cashOut: number;
  expectedCash: number;
  closingCash?: number | null;
  variance?: number | null;
  notes?: string | null;
  openedAt: string;
  closedAt?: string | null;
  movements?: CashMovement[];
}

interface CashHistoryEntry {
  id: number;
  sessionDate: string;
  openingCash: number;
  expectedCash: number;
  closingCash: number;
  variance: number;
  notes?: string | null;
}

// --- Component ---

const CashRegister: React.FC = () => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Data state
  const [session, setSession] = useState<CashSession | null>(null);
  const [sessionStatus, setSessionStatus] = useState<'NONE' | 'OPEN' | 'CLOSED'>('NONE');
  const [history, setHistory] = useState<CashHistoryEntry[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0 = Today, 1 = History
  const [openingCash, setOpeningCash] = useState<number | ''>('');
  const [closingCash, setClosingCash] = useState<number | ''>('');
  const [closingNotes, setClosingNotes] = useState('');
  const [pettyCashOpen, setPettyCashOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [revenueExpanded, setRevenueExpanded] = useState(false);
  const [revenueSubmitted, setRevenueSubmitted] = useState(false);

  // --- Data fetching ---

  const fetchSession = useCallback(async () => {
    try {
      const res = await cashApi.getCurrentSession();
      const data = res.data;
      // Backend returns { session: {...}, cashIn, cashOut, movements, ... } or the session directly
      const sessionData = data?.session || data;
      if (sessionData && sessionData.id) {
        // Merge cash totals into session for easy access
        setSession({
          ...sessionData,
          cashIn: data.cashIn ?? sessionData.cashIn ?? 0,
          cashOut: data.cashOut ?? sessionData.cashOut ?? 0,
          expectedCash: data.expectedCash ?? sessionData.expectedCash ?? sessionData.openingCash,
          movements: data.movements || [],
          pettyCashEntries: data.pettyCashEntries || [],
        });
        setSessionStatus(sessionData.status === 'CLOSED' ? 'CLOSED' : 'OPEN');
      } else {
        setSession(null);
        setSessionStatus('NONE');
      }
    } catch (err: unknown) {
      // 404 means no session today
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr?.response?.status === 404) {
        setSession(null);
        setSessionStatus('NONE');
      } else {
        console.error('Error fetching cash session:', err);
        toast.error('Failed to load cash register');
      }
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await cashApi.getCashHistory();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setHistory(data);
    } catch (err: unknown) {
      console.error('Error fetching cash history:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchSession();
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 1) {
      fetchHistory();
    }
  }, [tab, fetchHistory]);

  // --- Actions ---

  const handleOpenDay = async () => {
    if (!openingCash && openingCash !== 0) {
      toast.error('Enter opening cash amount');
      return;
    }
    setActionLoading(true);
    try {
      await cashApi.openDay({ openingCash: Number(openingCash) });
      toast.success('Register opened for the day');
      await fetchSession();
      setOpeningCash('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message = axiosErr?.response?.data?.message || 'Failed to open register';
      toast.error(message);
      // If session already exists, just refresh
      if (axiosErr?.response?.status === 400) {
        await fetchSession();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseDay = async () => {
    if (!closingCash && closingCash !== 0) {
      toast.error('Enter the actual closing cash');
      return;
    }
    setActionLoading(true);
    try {
      await cashApi.closeDay({
        closingCash: Number(closingCash),
        notes: closingNotes || null,
      });
      toast.success('Register closed for the day');
      await fetchSession();
      setClosingCash('');
      setClosingNotes('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { userMessage?: string; message?: string } } };
      const data = axiosErr?.response?.data;
      const message = data?.userMessage || data?.message || 'Failed to close register';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePettyCash = async (data: PettyCashFormData) => {
    await cashApi.addPettyCash({
      amount: Number(data.amount),
      reason: data.reason,
      takenBy: data.takenBy || null,
      convertToExpense: data.convertToExpense,
    });
    toast.success('Cash withdrawal recorded');
    await fetchSession();
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting...');
    try {
      const params: Record<string, unknown> = { format: 'CSV' };
      const res = await financeApi.exportData('day-end-reports', params);
      const filename = `cash-register-${todayLocalIso()}.csv`;
      downloadBlob(res.data, filename);
      toast.success('Downloaded!', { id: toastId });
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const handleReopenDay = async () => {
    setActionLoading(true);
    try {
      await cashApi.reopenDay();
      toast.success('New day opened');
      await fetchSession();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message = axiosErr?.response?.data?.message || 'Failed to open new day';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCashIn = () => {
    // Placeholder for manual cash-in recording
    toast('Manual cash-in coming soon. Use POS for sales.', { icon: '\uD83D\uDCB5' });
  };

  // --- Render helpers ---

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(CURRENCY.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getVarianceColor = (variance: number | null | undefined) => {
    if (variance === null || variance === undefined) return theme.gray?.[500] || '#666';
    if (variance === 0) return theme.success || '#4CAF50';
    if (variance < 0) return theme.danger || '#D99982';
    return '#2196F3'; // blue for positive variance (surplus)
  };

  const renderSummaryCard = (
    label: string,
    value: number,
    color?: string,
    bold?: boolean,
    icon?: React.ReactNode,
  ) => (
    <CardContainer
      disableHover
      sx={{
        p: { xs: 1.5, sm: 2 },
        flex: '1 0 auto',
        minWidth: { xs: 120, sm: 140 },
        maxWidth: { xs: 160, sm: 'unset' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{
            color: theme.gray?.[500] || '#666',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.6rem',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontWeight: bold ? 800 : 700,
          color: color || theme.text,
          fontSize: { xs: '1rem', sm: '1.25rem' },
          lineHeight: 1.2,
        }}
      >
        {formatCurrency(value, { showDecimals: false })}
      </Typography>
    </CardContainer>
  );

  // --- Loading state ---

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
        <LoadingSkeleton variant="card" count={4} />
      </Box>
    );
  }

  // --- NO SESSION: Start Day ---

  if (sessionStatus === 'NONE') {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
        <FinanceTabBar />

        {/* Header — confident-shell hero band */}
        <PageHeroBand eyebrow="Finance · Cash Register" title="Cash Register" />

        {/* Register Status Banner */}
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            backgroundColor: theme.gray?.[100] || '#F5F5F5',
            border: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
            No register open today
          </Typography>
        </Box>

        {/* Start Day Card */}
        <CardContainer
          disableHover
          sx={{
            p: { xs: 3, sm: 4 },
            maxWidth: 480,
            mx: 'auto',
            mt: { xs: 4, sm: 8 },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: theme.button.primary.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <BanknotesIcon style={{ width: 32, height: 32, color: theme.button.primary.text || '#FFFFFF' }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.text, mb: 0.5 }}>
            Open Register
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', mb: 3 }}>
            Count your cash drawer and enter the opening balance
          </Typography>

          <Box sx={{ mb: 2.5, textAlign: 'left' }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme.gray?.[500] || '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.75,
              }}
            >
              Opening Cash
            </Typography>
            <FormInput
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value ? Number(e.target.value) : '')}
              fullWidth
              autoFocus
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                      {CURRENCY.symbol}
                    </Typography>
                  </InputAdornment>
                ),
                sx: { fontSize: '1.5rem', fontWeight: 700, minHeight: 64 },
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>

          <PrimaryButton
            data-testid="open-register-button"
            fullWidth
            onClick={handleOpenDay}
            loading={actionLoading}
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              minHeight: 52,
              boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
              '&:hover': {
                boxShadow: '0 4px 12px color-mix(in srgb, var(--action) 40%, transparent)',
              },
            }}
          >
            {actionLoading ? 'Opening...' : 'Open Register'}
          </PrimaryButton>
        </CardContainer>

        {/* Quick link to history */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            startIcon={<ClockIcon style={{ width: 18, height: 18 }} />}
            onClick={() => { setTab(1); setSessionStatus('CLOSED'); }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: theme.text || '#333',
              minHeight: 44,
              '&:hover': { backgroundColor: `${theme.primary}11` },
              '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
            }}
          >
            View Past Sessions
          </Button>
        </Box>
      </Box>
    );
  }

  // --- MAIN RENDER (OPEN or CLOSED) ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band. Status chip + session date sit in
          the actions row so nothing from the old header is lost. */}
      <PageHeroBand
        eyebrow="Finance · Cash Register"
        title="Cash Register"
        heroLabel="Expected cash"
        heroValue={
          sessionStatus === 'OPEN' && session
            ? formatCurrency(session.expectedCash, { showDecimals: false })
            : undefined
        }
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={sessionStatus === 'OPEN' ? 'Open' : 'Closed'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  backgroundColor: sessionStatus === 'OPEN'
                    ? `${theme.success || '#4CAF50'}20`
                    : `${theme.gray?.[300] || '#CCC'}30`,
                  color: sessionStatus === 'OPEN'
                    ? theme.success || '#4CAF50'
                    : `color-mix(in srgb, var(--band-text) 75%, transparent)`,
                  border: `1px solid ${sessionStatus === 'OPEN' ? (theme.success || '#4CAF50') + '44' : (theme.gray?.[300] || '#CCC') + '66'}`,
                }}
              />
              {session?.openedAt && (
                <Typography variant="caption" sx={{ color: 'color-mix(in srgb, var(--band-text) 75%, transparent)' }}>
                  {formatDate(session.openedAt)}
                </Typography>
              )}
            </Box>

            {/* Desktop action buttons (only when open) — Cash In keeps its green
                outline (legible on the dark band); Cash Out keeps saffron fill. */}
            {sessionStatus === 'OPEN' && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, flexShrink: 0 }}>
                <Button
                  data-testid="cash-in-button"
                  variant="outlined"
                  startIcon={<ArrowDownTrayIcon style={{ width: 20, height: 20 }} />}
                  onClick={handleAddCashIn}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    minHeight: 44,
                    color: theme.success || '#4CAF50',
                    border: `1.5px solid ${theme.success || '#4CAF50'}`,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      borderColor: theme.success || '#4CAF50',
                      backgroundColor: `${theme.success || '#4CAF50'}08`,
                    },
                    '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                  }}
                >
                  Cash In
                </Button>
                <PrimaryButton
                  data-testid="cash-out-button"
                  startIcon={<ArrowUpTrayIcon style={{ width: 20, height: 20, color: 'var(--text-inverse)' }} />}
                  onClick={() => setPettyCashOpen(true)}
                  sx={{
                    minHeight: 44,
                    boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
                  }}
                >
                  Cash Out
                </PrimaryButton>
              </Box>
            )}
          </Box>
        }
      />

      {/* Register Status Banner */}
      {sessionStatus === 'OPEN' && session && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            backgroundColor: `${theme.success || '#4CAF50'}10`,
            border: `1px solid ${theme.success || '#4CAF50'}30`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: theme.text, fontWeight: 600 }}>
            Register Open
          </Typography>
          {session.openedAt && (
            <Typography sx={{ fontSize: '0.813rem', color: theme.gray?.[500] || '#666' }}>
              — since {(() => {
                try {
                  return new Date(session.openedAt).toLocaleTimeString(CURRENCY.locale, { hour: '2-digit', minute: '2-digit' });
                } catch {
                  return session.openedAt;
                }
              })()}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.813rem', color: theme.gray?.[500] || '#666' }}>
            — Opening balance: {formatCurrency(session.openingCash, { showDecimals: false })}
          </Typography>
        </Box>
      )}
      {sessionStatus === 'CLOSED' && session && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            backgroundColor: theme.gray?.[100] || '#F5F5F5',
            border: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
            Register closed for today
          </Typography>
        </Box>
      )}

      {/* Tabs: Today / History */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 40,
          backgroundColor: `${theme.primary} !important`,
          borderRadius: '10px',
          padding: '2px',
          width: 'fit-content',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'rgba(255,255,255,0.7) !important',
            minHeight: 36,
            borderRadius: '8px',
            zIndex: 1,
            '&.Mui-selected': { color: 'var(--text-inverse) !important', backgroundColor: 'rgba(255,255,255,0.15) !important' },
          },
          '& .MuiTabs-indicator': { display: 'none' },
        }}
      >
        <Tab label="Today" />
        <Tab label="History" />
      </Tabs>

      {/* TAB 0: Today */}
      {tab === 0 && session && (
        <>
          {/* Summary Cards */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 1.5 },
              mb: { xs: 2, sm: 3 },
              overflowX: 'auto',
              pb: 0.5,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme.gray?.[300] || '#CCC', borderRadius: 2 },
            }}
          >
            {renderSummaryCard('Opening', session.openingCash)}
            {renderSummaryCard(
              'Cash In',
              session.cashIn,
              theme.success || '#4CAF50',
              false,
              <ArrowDownTrayIcon style={{ width: 14, height: 14, color: theme.success || '#4CAF50' }} />,
            )}
            {renderSummaryCard(
              'Cash Out',
              session.cashOut,
              theme.danger || '#D99982',
              false,
              <ArrowUpTrayIcon style={{ width: 14, height: 14, color: theme.danger || '#D99982' }} />,
            )}
            {renderSummaryCard('Expected', session.expectedCash, theme.text, true)}
            {sessionStatus === 'CLOSED' && session.closingCash != null && (
              <>
                {renderSummaryCard('Actual', session.closingCash)}
                {renderSummaryCard(
                  'Variance',
                  session.variance || 0,
                  getVarianceColor(session.variance),
                  true,
                  session.variance === 0 ? (
                    <CheckCircleIcon style={{ width: 14, height: 14, color: theme.success || '#4CAF50' }} />
                  ) : (
                    <ExclamationTriangleIcon style={{ width: 14, height: 14, color: getVarianceColor(session.variance) }} />
                  ),
                )}
              </>
            )}
          </Box>

          {/* Open New Day button (when CLOSED) */}
          {sessionStatus === 'CLOSED' && (
            <CardContainer
              disableHover
              sx={{
                p: { xs: 2.5, sm: 3 },
                mb: 2,
                textAlign: 'center',
                borderColor: `${theme.primary}44`,
                backgroundColor: `${theme.primary}08`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.text, mb: 0.5 }}>
                Register is closed for today
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', mb: 2, fontSize: '0.813rem' }}>
                Open a new day to start recording transactions
              </Typography>
              <PrimaryButton
                data-testid="reopen-day-button"
                onClick={handleReopenDay}
                loading={actionLoading}
                startIcon={!actionLoading ? <BanknotesIcon style={{ width: 20, height: 20, color: 'var(--text-inverse)' }} /> : undefined}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  minHeight: 48,
                  px: 4,
                  boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
                }}
              >
                {actionLoading ? 'Opening...' : 'Open New Day'}
              </PrimaryButton>
            </CardContainer>
          )}

          {/* Closed session notes */}
          {sessionStatus === 'CLOSED' && session.notes && (
            <CardContainer disableHover sx={{ mb: 2, p: 2 }}>
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                Closing Notes
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text, mt: 0.5 }}>
                {session.notes}
              </Typography>
            </CardContainer>
          )}

          {/* Cash Movements Timeline */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.text, mb: 1.5, fontSize: '0.95rem' }}
            >
              Cash Movements
            </Typography>
            <CashMovementList
              movements={session.movements || []}
              loading={false}
            />
          </Box>

          {/* Mobile action buttons (when OPEN) */}
          {sessionStatus === 'OPEN' && isMobile && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                data-testid="cash-in-button-mobile"
                variant="outlined"
                fullWidth
                startIcon={<ArrowDownTrayIcon style={{ width: 18, height: 18 }} />}
                onClick={handleAddCashIn}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  minHeight: 44,
                  color: theme.success || '#4CAF50',
                  border: `1.5px solid ${theme.success || '#4CAF50'}`,
                  backgroundColor: 'transparent',
                  '&:hover': { borderColor: theme.success || '#4CAF50' },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                Cash In
              </Button>
              <Button
                data-testid="cash-out-button-mobile"
                variant="outlined"
                fullWidth
                startIcon={<ArrowUpTrayIcon style={{ width: 18, height: 18 }} />}
                onClick={() => setPettyCashOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  minHeight: 44,
                  color: theme.danger || '#D99982',
                  border: `1.5px solid ${theme.danger || '#D99982'}`,
                  backgroundColor: 'transparent',
                  '&:hover': { borderColor: theme.danger || '#D99982' },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                Cash Out
              </Button>
            </Box>
          )}

          {/* Close Day Section (when OPEN) */}
          {sessionStatus === 'OPEN' && (
            <CardContainer
              data-testid="close-register-section"
              disableHover
              sx={{
                p: { xs: 2.5, sm: 3 },
                mt: 2,
                borderColor: `${theme.warning || '#FFC107'}44`,
                backgroundColor: `${theme.warning || '#FFC107'}08`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.text, mb: 0.5 }}>
                Close Register
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', mb: 2, fontSize: '0.813rem' }}>
                Count your cash drawer and enter the closing amount. Expected: {formatCurrency(session.expectedCash, { showDecimals: false })}
              </Typography>

              {/* Revenue Breakdown (optional) */}
              <Box sx={{ mb: 2 }}>
                <Button
                  onClick={() => setRevenueExpanded((prev) => !prev)}
                  startIcon={
                    revenueExpanded ? (
                      <ChevronUpIcon style={{ width: 18, height: 18 }} />
                    ) : (
                      <ChevronDownIcon style={{ width: 18, height: 18 }} />
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: revenueSubmitted ? (theme.success || '#4CAF50') : theme.primary,
                    justifyContent: 'flex-start',
                    px: 1,
                    py: 0.5,
                    mb: revenueExpanded ? 1.5 : 0,
                    minHeight: 44,
                    '&:hover': { backgroundColor: `${theme.primary}11` },
                    '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                  }}
                >
                  {revenueSubmitted ? 'Revenue Breakdown (saved)' : 'Revenue Breakdown (optional)'}
                </Button>

                {revenueExpanded && session.id && (
                  <RevenueBreakdownForm
                    daySessionId={session.id}
                    date={todayLocalIso()}
                    onSubmit={() => setRevenueSubmitted(true)}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormInput
                  label="Closing Cash *"
                  type="number"
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value ? Number(e.target.value) : '')}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                          {CURRENCY.symbol}
                        </Typography>
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.25rem', fontWeight: 700, minHeight: 56 },
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />

                {/* Variance preview */}
                {closingCash !== '' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666' }}>
                      Variance:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: getVarianceColor(Number(closingCash) - session.expectedCash),
                      }}
                    >
                      {formatCurrency(Number(closingCash) - session.expectedCash, { showDecimals: false })}
                    </Typography>
                  </Box>
                )}

                <FormInput
                  label="Notes (optional)"
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={3}
                  placeholder="e.g. Short by 50 due to change given"
                />

                <PrimaryButton
                  data-testid="close-day-button"
                  fullWidth
                  onClick={handleCloseDay}
                  disabled={!closingCash && closingCash !== 0}
                  loading={actionLoading}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    minHeight: 48,
                  }}
                >
                  {actionLoading ? 'Closing...' : 'Close Day'}
                </PrimaryButton>
              </Box>
            </CardContainer>
          )}
        </>
      )}

      {/* TAB 1: History */}
      {tab === 1 && (
        <Box>
          {history.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <AppIconButton
                icon={<ArrowDownTrayIcon style={{ width: 18, height: 18 }} />}
                tooltip="Export CSV"
                variant="utility"
                size="small"
                onClick={handleExportCsv}
                disabled={exporting}
                sx={{
                  border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                  borderRadius: '10px',
                  width: 36,
                  height: 36,
                }}
              />
            </Box>
          )}
          {history.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>{'\uD83D\uDCC5'}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
                No past sessions
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666' }}>
                Your cash register history will appear here
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {history.map((entry) => (
                <CardContainer
                  key={entry.id}
                  disableHover
                  sx={{
                    p: 2,
                    cursor: 'default',
                  }}
                >
                  {/* Date header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                      {formatDate(entry.sessionDate)}
                    </Typography>
                    <Chip
                      label={
                        entry.variance === 0
                          ? 'Balanced'
                          : entry.variance > 0
                            ? `+${formatCurrency(entry.variance, { showDecimals: false, showSymbol: false })}`
                            : formatCurrency(entry.variance, { showDecimals: false, showSymbol: false })
                      }
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        backgroundColor: `${getVarianceColor(entry.variance)}20`,
                        color: getVarianceColor(entry.variance),
                        border: `1px solid ${getVarianceColor(entry.variance)}44`,
                      }}
                    />
                  </Box>

                  {/* Details row */}
                  <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                        Opening
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                        {formatCurrency(entry.openingCash, { showDecimals: false })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                        Expected
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                        {formatCurrency(entry.expectedCash, { showDecimals: false })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                        Actual
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                        {formatCurrency(entry.closingCash, { showDecimals: false })}
                      </Typography>
                    </Box>
                  </Box>

                  {entry.notes && (
                    <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', mt: 1, display: 'block', fontStyle: 'italic' }}>
                      {entry.notes}
                    </Typography>
                  )}
                </CardContainer>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Mobile FAB for Cash Out (when session is open) */}
      {isMobile && sessionStatus === 'OPEN' && tab === 0 && (
        <Fab
          onClick={() => setPettyCashOpen(true)}
          aria-label="Cash Out"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            zIndex: 1000,
            backgroundColor: theme.button?.primary?.bg || 'var(--action)',
            color: theme.button?.primary?.text || '#FFFFFF',
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px color-mix(in srgb, var(--action) 40%, transparent)',
            '&:hover': {
              backgroundColor: theme.button?.primary?.hover || 'var(--action-hover)',
              boxShadow: '0 6px 20px color-mix(in srgb, var(--action) 50%, transparent)',
            },
          }}
        >
          <PlusIcon style={{ width: 28, height: 28 }} />
        </Fab>
      )}

      {/* Petty Cash Modal */}
      <PettyCashModal
        open={pettyCashOpen}
        onClose={() => setPettyCashOpen(false)}
        onSave={handlePettyCash}
      />
    </Box>
  );
};

export default CashRegister;
